import { faDiamond } from "@jewlr/sharp-solid-svg-icons/faDiamond"
import { useMemo } from "react"
import { shallowEqual, useSelector } from "react-redux"

import {
  isStoneOrAccentPref,
  makeShouldUseNewWizardExp,
} from "areas/product/jewlr"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { jewlr_t } from "helpers/application"
import { cn } from "styles/utils"

import WizardLoading from "./loaders/wizard-loading"
import AccordionContent from "./wizard-options/accordion-content"
import AccordionHeading from "./wizard-options/accordion-heading"
import GuidedStoneSelectorWizard from "./wizard-options/guided-stone-selector/wizard"
import MetalSelectionStatic from "./wizard-options/metal-selection-static"
// import UnifiedStonePicker from "./wizard-options/unified-stone-picker"

const makeShouldHidePref = (wizardData) => (pref) =>
  typeof wizardData[pref]?.heading === "undefined" ||
  wizardData[pref].heading?.disabled ||
  wizardData[pref]?.disabled ||
  (/^s\d+$/.test(pref) &&
    wizardData[pref].options.length === 0 &&
    !wizardData[pref]?.override_text)

// Transform prefOrder from `['sku', 'o1', 's1', 's2', 'as', 'ef', 'sz', 'box']`
// To `['sku', 'o1', ['s1', 's2', 'as'], 'ef', 'sz', 'box']`
// Not use guided stone selector if there are fewer than 2 stones
const transformGuidedStoneSelectorPrefOrder = (prefOrder, wizardData) => {
  const shouldHidePref = makeShouldHidePref(wizardData)

  const stonePrefs = []
  const otherPrefs = []
  let insertIndex = -1

  for (const pref of prefOrder) {
    if (shouldHidePref(pref)) continue

    if (isStoneOrAccentPref(pref)) {
      if (insertIndex === -1) {
        insertIndex = otherPrefs.length
      }
      stonePrefs.push(pref)
    } else {
      otherPrefs.push(pref)
    }
  }

  if (insertIndex === -1 || stonePrefs.length < 2) {
    return prefOrder.filter((pref) => !shouldHidePref(pref))
  }

  return [
    ...otherPrefs.slice(0, insertIndex),
    stonePrefs,
    ...otherPrefs.slice(insertIndex),
  ]
}

const WizardOptions = () => {
  const {
    adminMode,
    data: wizardData,
    loading: wizardLoading,
    prefOrder,
  } = useSelector((state) => state.wizard, shallowEqual)
  const shouldUseNewWizardExp = makeShouldUseNewWizardExp()

  const transformedPrefOrder = useMemo(() => {
    if (wizardLoading || !wizardData || !prefOrder) return []

    if (!shouldUseNewWizardExp) return prefOrder

    return transformGuidedStoneSelectorPrefOrder(prefOrder, wizardData)
  }, [shouldUseNewWizardExp, prefOrder, wizardData, wizardLoading])

  // Check if a pref qualifies for unified stone picker
  // const shouldUseUnifiedStonePicker = (pref) => {
  //   if (pref === "sc") return true
  //   if (pref === "o1") {
  //     const o1Data = wizardData?.o1
  //     const hasStoneOptions = Object.keys(wizardData || {}).some((key) =>
  //       /^s\d+$/.test(key)
  //     )
  //     return (
  //       o1Data &&
  //       o1Data.title?.toLowerCase().includes("number") &&
  //       hasStoneOptions
  //     )
  //   }
  //   return false
  // }

  // const useUnifiedStonePickerVariant =
  //   unifiedStonePickerExp === 1 &&
  //   prefOrder.some((pref) => shouldUseUnifiedStonePicker(pref))

  return (
    <div
      className={cn(
        "tablet:border-t-grey-10 tablet:border-t",
        shouldUseNewWizardExp ? "overflow-visible" : "overflow-hidden"
      )}
    >
      <div
        className={cn(
          "flex items-center py-12",
          !shouldUseNewWizardExp && "tablet:hidden border-b-grey-10 border-b"
        )}
      >
        <FontAwesomeIcon
          className="text-12 text-primary mr-6"
          icon={faDiamond}
        />
        <h2 className="text-16 font-semibold">
          {shouldUseNewWizardExp
            ? `Start Your ${jewlr_t("terms.Personalization")}`
            : `${jewlr_t("terms.Personalize")} It`}
        </h2>
      </div>
      {wizardLoading ? (
        <WizardLoading />
      ) : (
        // ) : useUnifiedStonePickerVariant ? (
        //   prefOrder
        //     .filter((pref) => !(adminMode && pref == "gift"))
        //     .filter((pref) => !isStonePref(pref))
        //     .map((pref) => {
        //       if (shouldUseUnifiedStonePicker(pref)) {
        //         return (
        //           <div data-cy={`wizard-accordion-${pref}`} key={`wopt-${pref}`}>
        //             <UnifiedStonePicker panel={pref} pref={pref} />
        //           </div>
        //         )
        //       }

        //       // Static metal selection for ab test
        //       // if (index === 0 && pref === "sku") {
        //       //   return <MetalSelectionStatic key={`wopt-${pref}`} pref={pref} />
        //       // }

        //       // Render other prefs normally
        //       return (
        //         <div data-cy={`wizard-accordion-${pref}`} key={`wopt-${pref}`}>
        //           <AccordionHeading pref={pref} />
        //           <AccordionContent panel={pref} pref={pref} />
        //         </div>
        //       )
        //     })
        transformedPrefOrder
          .filter((pref) => !(adminMode && pref === "gift"))
          .map((pref, i) => {
            if (pref === "sku" && i === 0 && !shouldUseNewWizardExp) {
              return <MetalSelectionStatic key={`wopt-${pref}`} pref={pref} />
            }

            if (Array.isArray(pref)) {
              return (
                <GuidedStoneSelectorWizard
                  key="wopt-guided-stone"
                  prefs={pref}
                />
              )
            }

            return (
              <div
                className={cn(
                  shouldUseNewWizardExp && "border-b-grey-10 border-b"
                )}
                data-cy={`wizard-accordion-${pref}`}
                key={`wopt-${pref}`}
              >
                <AccordionHeading pref={pref} />
                <AccordionContent index={i} panel={pref} pref={pref} />
              </div>
            )
          })
      )}
    </div>
  )
}

export default WizardOptions
